/**
 * Storage transfer helpers — pure logic + axios-based upload/download.
 * Uses the existing apiClient from src/lib/api.ts.
 */

import { apiClient } from './api';

const MULTIPART_THRESHOLD = 50 * 1024 * 1024; // 50 MB

/**
 * Timeout for upload requests that transport file bodies (single-file and
 * per-part PUTs). Raised well beyond the shared apiClient default (30s) so
 * large files / parts are not interrupted mid-transfer.
 */
export const UPLOAD_TIMEOUT_MS = 30 * 60 * 1000; // 30 minutes

// ── Upload planning ──────────────────────────────────────────────────────────

export interface UploadPlanSingle {
  mode: 'single';
}

export interface UploadPlanMultipart {
  mode: 'multipart';
  partCount: number;
  parts: Array<{ partNumber: number; start: number; end: number; size: number }>;
}

export type UploadPlan = UploadPlanSingle | UploadPlanMultipart;

export function planUpload(file: File, partSizeMB: number = 10): UploadPlan {
  if (file.size <= MULTIPART_THRESHOLD) {
    return { mode: 'single' };
  }

  const partSizeBytes = partSizeMB * 1024 * 1024;
  const partCount = Math.ceil(file.size / partSizeBytes);
  const parts: UploadPlanMultipart['parts'] = [];

  for (let i = 0; i < partCount; i++) {
    const start = i * partSizeBytes;
    const end = Math.min(start + partSizeBytes, file.size);
    parts.push({
      partNumber: i + 1,
      start,
      end,
      size: end - start,
    });
  }

  return { mode: 'multipart', partCount, parts };
}

// ── SHA-256 computation (streamed via crypto.subtle) ─────────────────────────

export async function computeSha256(file: File): Promise<string> {
  const buffer = await file.arrayBuffer();
  const hashBuffer = await crypto.subtle.digest('SHA-256', buffer);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
}

// ── Single-part upload ───────────────────────────────────────────────────────

export async function uploadSingle(
  file: File,
  collectionId: string,
  onProgress?: (percent: number) => void,
  description?: string,
): Promise<{ fileId: string }> {
  try {
    const response = await apiClient.post(
      `/storage/collections/${collectionId}/files`,
      file,
      {
        timeout: UPLOAD_TIMEOUT_MS,
        headers: {
          // The backend's raw body parser only accepts application/octet-stream.
          // Using a browser MIME type (e.g. text/plain, image/png) leaves
          // req.body unparsed as {} and the upload fails with a 500.
          'Content-Type': 'application/octet-stream',
          'x-file-name': file.name,
          ...(description?.trim() ? { 'x-file-description': description.trim() } : {}),
        },
        onUploadProgress: (event) => {
          if (event.total && onProgress) {
            onProgress(Math.round((event.loaded / event.total) * 100));
          }
        },
      },
    );
    // The storage service returns the created file document, whose id lives in
    // `_id` (not `fileId`). Normalize both shapes so callers that destructure
    // `{ fileId }` (e.g. the voice-sample uploader) get a usable id.
    const data = (response.data ?? {}) as { fileId?: string; _id?: string; id?: string };
    const fileId = data.fileId ?? data._id ?? data.id;
    if (!fileId) {
      throw new Error('Upload succeeded but no file id was returned');
    }
    return { fileId };
  } catch (err: any) {
    const status = err?.response?.status;
    if (status === 413) {
      throw new Error('File exceeds maximum size limit');
    }
    if (status === 416) {
      throw new Error('Invalid byte range requested');
    }
    throw err;
  }
}

// ── Multipart upload ─────────────────────────────────────────────────────────

export async function uploadMultipart(
  file: File,
  collectionId: string,
  sha256: string,
  partSizeMB: number = 10,
  onProgress?: (percent: number) => void,
  signal?: AbortSignal,
): Promise<{ fileId: string }> {
  const plan = planUpload(file, partSizeMB);
  if (plan.mode !== 'multipart') {
    throw new Error('File does not require multipart upload');
  }

  // Create upload session
  const { data: upload } = await apiClient.post(
    `/storage/uploads`,
    {
      collectionId,
      fileName: file.name,
      size: file.size,
      sha256,
    },
    { timeout: UPLOAD_TIMEOUT_MS },
  );

  const uploadId = upload._id || upload.id;

  try {
    let uploadedBytes = 0;

    // Upload parts sequentially
    for (const part of plan.parts) {
      if (signal?.aborted) {
        await apiClient.delete(
          `/storage/uploads/${uploadId}`,
        );
        throw new Error('Upload aborted');
      }

      const slice = file.slice(part.start, part.end);
      const bytesBeforePart = uploadedBytes;
      await apiClient.put(
        `/storage/uploads/${uploadId}/parts/${part.partNumber}`,
        slice,
        {
          timeout: UPLOAD_TIMEOUT_MS,
          headers: {
            'Content-Type': 'application/octet-stream',
            'Content-Length': String(part.size),
          },
          onUploadProgress: (progressEvent) => {
            if (onProgress) {
              const partLoaded = bytesBeforePart + progressEvent.loaded;
              onProgress(Math.round((partLoaded / file.size) * 100));
            }
          },
        },
      );

      uploadedBytes += part.size;
      if (onProgress) {
        onProgress(Math.round((uploadedBytes / file.size) * 100));
      }
    }

    // Complete upload
    const { data: completed } = await apiClient.post(
      `/storage/uploads/${uploadId}/complete`,
      undefined,
      { timeout: UPLOAD_TIMEOUT_MS },
    );
    return completed;
  } catch (err: any) {
    // Abort on error
    try {
      await apiClient.delete(
        `/storage/uploads/${uploadId}`,
      );
    } catch {
      // Best effort abort
    }
    const status = err?.response?.status;
    if (status === 413) {
      throw new Error('File exceeds maximum size limit');
    }
    if (status === 416) {
      throw new Error('Invalid byte range requested');
    }
    throw err;
  }
}

// ── Download ─────────────────────────────────────────────────────────────────

export async function downloadFile(
  fileId: string,
  opts?: { range?: string },
): Promise<{ blob: Blob; headers: Record<string, string> }> {
  const headers: Record<string, string> = {};
  if (opts?.range) {
    headers['Range'] = opts.range;
  }

  let response;
  try {
    response = await apiClient.get(`/storage/files/${fileId}/content`, {
      responseType: 'blob',
      headers,
    });
  } catch (err: any) {
    // When responseType is 'blob', a non-2xx body is returned as a Blob whose
    // payload is serialized JSON (e.g. { error: { code, message, requestId } }).
    // Extract the real message so downstream catch blocks surface it instead of
    // falling back to a generic error.
    const data: unknown = err?.response?.data;
    if (data && typeof (data as Blob).text === 'function') {
      try {
        const text = await (data as Blob).text();
        const parsed = JSON.parse(text) as {
          error?: { message?: string };
          message?: string;
        };
        const message = parsed?.error?.message ?? parsed?.message;
        if (message) {
          throw new Error(message);
        }
      } catch {
        // Body wasn't JSON or contained no message — propagate the original error.
      }
    }
    throw err;
  }

  const responseHeaders: Record<string, string> = {};
  if (response.headers) {
    for (const [key, value] of Object.entries(response.headers)) {
      if (typeof value === 'string') {
        responseHeaders[key] = value;
      }
    }
  }

  return { blob: response.data, headers: responseHeaders };
}

// ── Blob save helper ─────────────────────────────────────────────────────────

export function saveBlob(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  anchor.style.display = 'none';
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
}

// ── Formatting helpers ───────────────────────────────────────────────────────

export function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB', 'PB'];
  const k = 1024;
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  const value = bytes / Math.pow(k, i);
  return `${value.toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
}
